import { useState } from "react";
import { History, Loader2, MessageSquareText, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const ta = "w-full rounded-md border border-slate-200 p-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-600/30";

// v1.27.0 açıklama düzenleme izni: siparisler:edit VEYA siparişin temsilcisi; iptal salt-okunur (sunucu da doğrular)
export const useCanEditNotes = (o) => {
  const { can, user } = useAuth();
  return o.status !== "iptal" && user?.role_key !== "inceleme" && (can("siparisler", "edit") || o.sales_rep_id === user?.id);
};

export async function saveNotes(o, patch, onSaved) {
  try {
    const { data } = await api.patch(`/orders/${o.id}/notes`, { notes_rev: o.notes_rev || 0, ...patch });
    onSaved(data);
    toast.success("Açıklama kaydedildi");
    return true;
  } catch (e) { toast.error(apiError(e)); return false; }
}

function NoteDialog({ title, value, max, onClose, onSave, testid }) {
  const [v, setV] = useState(value || "");
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open onOpenChange={(x) => !x && onClose()}>
      <DialogContent className="max-w-lg" data-testid={`${testid}-dialog`}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <textarea autoFocus rows={max > 500 ? 6 : 3} maxLength={max} value={v} onChange={(e) => setV(e.target.value)} className={ta} data-testid={`${testid}-input`} />
        <p className="text-[11px] text-slate-500">{v.length}/{max} · Yalnız iç/lojistik not: tutar, satır, stok, cari ve belge numaraları değişmez.</p>
        <DialogFooter>
          {value && <Button variant="ghost" className="text-rose-700 mr-auto" onClick={() => setV("")} data-testid={`${testid}-clear`}>Temizle</Button>}
          <Button variant="outline" onClick={onClose}>Vazgeç</Button>
          <Button className="bg-blue-600 hover:bg-blue-700" disabled={busy} onClick={async () => { setBusy(true); if (await onSave(v.trim() || null)) onClose(); setBusy(false); }} data-testid={`${testid}-save`}>
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Kaydet</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function LineNoteEdit({ o, idx, onSaved }) {
  const [open, setOpen] = useState(false);
  const l = o.lines[idx];
  if (!useCanEditNotes(o)) return null;
  return (
    <>
      <button onClick={() => setOpen(true)} title="Ürün açıklaması" className={`ml-1 inline-flex align-middle ${l.note ? "text-amber-700" : "text-slate-300 hover:text-slate-600"}`} data-testid={`order-line-note-edit-${l.sku}`}><MessageSquareText className="h-3.5 w-3.5" /></button>
      {open && <NoteDialog title={`Ürün Açıklaması · ${l.sku}`} value={l.note} max={500} testid="line-note" onClose={() => setOpen(false)}
        onSave={(note) => saveNotes(o, { lines: [{ idx, note }] }, onSaved)} />}
    </>
  );
}

export default function OrderNotesCard({ o, onSaved }) {
  const [open, setOpen] = useState(false);
  const canEdit = useCanEditNotes(o);
  const hist = [...(o.notes_history || [])].reverse();
  return (
    <div className="panel p-3" data-testid="order-notes-card">
      <div className="flex items-center justify-between">
        <div className="text-[12px] font-semibold text-slate-600 flex items-center gap-1"><MessageSquareText className="h-4 w-4" />Açıklama</div>
        {canEdit && <Button size="sm" variant="outline" className="h-7" onClick={() => setOpen(true)} data-testid="order-note-edit-button"><Pencil className="h-3.5 w-3.5 mr-1" />{o.note ? "Düzenle" : "Ekle"}</Button>}
      </div>
      <div className={`mt-1 text-[13px] whitespace-pre-wrap ${o.note ? "text-slate-900" : "text-slate-400"}`} data-testid="order-note-text">{o.note || "Açıklama yok"}</div>
      {hist.length > 0 && (
        <details className="mt-2" data-testid="order-notes-history">
          <summary className="text-[11px] text-slate-500 cursor-pointer flex items-center gap-1"><History className="h-3.5 w-3.5" />Değişiklik geçmişi ({hist.length})</summary>
          <ul className="mt-1 space-y-1">{hist.map((h, i) => (
            <li key={i} className="text-[11px] text-slate-600 border-l-2 border-slate-200 pl-2" data-testid={`order-notes-history-${i}`}>
              <span className="font-mono">{formatDate(h.at)}</span> · {h.user_name} · {h.target === "order" ? "Sipariş açıklaması" : `Ürün açıklaması ${h.sku}`}:{" "}
              <span className="line-through text-slate-400">{h.old || "—"}</span> → <span className="text-slate-900">{h.new || "—"}</span>
            </li>))}</ul>
        </details>
      )}
      {open && <NoteDialog title="Sipariş Açıklaması" value={o.note} max={2000} testid="order-note" onClose={() => setOpen(false)} onSave={(note) => saveNotes(o, { note }, onSaved)} />}
    </div>
  );
}
